import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Unlock, Monitor, Mail, RefreshCw } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function SecurityAdmin() {
  const { t } = useI18n();
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlocked = () => {
    setLoading(true);
    api.get('/security/blocked')
      .then(r => setBlocked(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBlocked(); }, []);

  const handleUnblock = async (key) => {
    try {
      await api.post('/security/unblock', { key });
      toast.success(t('security.unblocked'));
      fetchBlocked();
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="security-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#CE1021]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#CE1021]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('security.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('security.subtitle')}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchBlocked} data-testid="refresh-blocked-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" />{t('common.refresh')}
        </Button>
      </div>

      <Card className="border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('security.blocked_title')}
            <Badge variant="secondary" className="ml-1">{blocked.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-3 border-[#007FFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : blocked.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="w-10 h-10 text-[#009543] mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">{t('security.no_blocked')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {blocked.map((entry, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-destructive/5 border-destructive/20" data-testid={`blocked-entry-${i}`}>
                  <div className="flex items-center gap-3">
                    {entry.type === 'ip' ? (
                      <Monitor className="w-5 h-5 text-[#CE1021]" />
                    ) : (
                      <Mail className="w-5 h-5 text-[#CE1021]" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{entry.value}</p>
                      <p className="text-xs text-muted-foreground">
                        {entry.failures} {t('security.failures')} &middot; {t('security.blocked_for')} {entry.remaining_minutes} min
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleUnblock(entry.key)} data-testid={`unblock-btn-${i}`}>
                    <Unlock className="w-4 h-4 mr-1" />{t('security.unblock')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('security.rules_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">5 {t('security.failures')}</Badge>
              <span className="text-muted-foreground">{t('security.rule_5')}</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">10 {t('security.failures')}</Badge>
              <span className="text-muted-foreground">{t('security.rule_10')}</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Badge variant="outline" className="shrink-0">20+ {t('security.failures')}</Badge>
              <span className="text-muted-foreground">{t('security.rule_20')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
